import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

interface AuthGuardProps {
  children: React.ReactNode;
}

export function AuthGuard({ children }: AuthGuardProps) {
  const { isAuthenticated, loading, initialized } = useAuth();
  const location = useLocation();
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (initialized && !loading && !isAuthenticated) {
      sessionStorage.setItem('redirectAfterLogin', location.pathname);
      const isAccountRoute = location.pathname.startsWith('/account/');
      const redirectTarget = isAccountRoute ? '/account/login' : '/account/register';
      const redirectMessage = isAccountRoute ? '正在導向登入頁面...' : '正在導向註冊頁面...';
      setShowModal(true);
      const timer = setTimeout(() => {
        window.REACT_APP_NAVIGATE(redirectTarget);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, loading, initialized, location.pathname]);

  // 載入中
  if (!initialized || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-gray-200 border-t-black rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">載入中...</p>
        </div>
      </div>
    );
  }

  // 未登入 → 顯示提示並跳轉
  if (!isAuthenticated && showModal) {
    const isAccountRoute = location.pathname.startsWith('/account/');
    const redirectMessage = isAccountRoute ? '正在導向登入頁面...' : '正在導向註冊頁面...';

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 animate-[fadeIn_0.3s_ease-out]">
          <div className="text-center">
            <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-lock-line text-3xl text-orange-600"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">需要登入</h3>
            <p className="text-gray-600 mb-6">
              您需要先註冊或登入才能查看此頁面
            </p>
            <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
              <div className="w-2 h-2 bg-orange-600 rounded-full animate-bounce"></div>
              <div className="w-2 h-2 bg-orange-600 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
              <div className="w-2 h-2 bg-orange-600 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
              <span className="ml-2">{redirectMessage}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}